import { useMemo, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EmptyState, InfoCard } from '@backstage/core-components';
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';

export const BROKER_RUNTIME_CONFIG_ANNOTATION =
  'streamnative.io/broker-runtime-config-json';
export const BROKER_DYNAMIC_CONFIG_ANNOTATION =
  'streamnative.io/broker-dynamic-config-json';

const useStyles = makeStyles(theme => ({
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  filter: {
    flex: 1,
    minWidth: 0,
  },
  hint: {
    marginBottom: theme.spacing(1),
  },
  list: {
    overflow: 'auto',
    maxHeight: 'calc(100vh - 320px)',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dot: {
    width: 8,
    height: 8,
    marginTop: 6,
    borderRadius: '50%',
    backgroundColor: '#81c784',
    flexShrink: 0,
  },
  dotSpacer: {
    width: 8,
    flexShrink: 0,
  },
  key: {
    flex: '0 0 42%',
    fontFamily: 'ui-monospace, Menlo, Monaco, monospace',
    fontSize: 13,
    wordBreak: 'break-word',
  },
  value: {
    flex: 1,
    fontFamily: 'ui-monospace, Menlo, Monaco, monospace',
    fontSize: 13,
    wordBreak: 'break-word',
  },
  runtimeValue: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
}));

type FlatEntry = { key: string; value: string };

export type ConfigRow = FlatEntry & {
  dynamic: boolean;
  /** Set when this runtime key is overridden by a different dynamic value. */
  dynamicValue?: string;
};

type ParsedConfig =
  | { kind: 'empty' }
  | { kind: 'ok'; value: unknown }
  | { kind: 'error'; message: string; raw: string };

function parseConfig(raw: string | undefined): ParsedConfig {
  if (!raw?.trim()) {
    return { kind: 'empty' };
  }
  try {
    return { kind: 'ok', value: JSON.parse(raw) as unknown };
  } catch (e) {
    return {
      kind: 'error',
      message: e instanceof Error ? e.message : String(e),
      raw,
    };
  }
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined) return '';
  return JSON.stringify(value);
}

/** Flattens nested objects into dot-path rows. Arrays stay a single value. */
export function flattenConfig(value: unknown, prefix = ''): FlatEntry[] {
  if (value === undefined) return [];
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>).sort(
      ([a], [b]) => a.localeCompare(b),
    );
    if (entries.length === 0) {
      return prefix ? [{ key: prefix, value: '{}' }] : [];
    }
    return entries.flatMap(([key, child]) =>
      flattenConfig(child, prefix ? `${prefix}.${key}` : key),
    );
  }
  return [{ key: prefix || '(root)', value: formatValue(value) }];
}

/** Runtime rows with dynamic keys marked and overrides inlined. */
export function configRows(runtime: unknown, dynamic: unknown): ConfigRow[] {
  const dynamicEntries = new Map(
    flattenConfig(dynamic).map(entry => [entry.key, entry.value]),
  );
  const runtimeEntries = new Map(
    flattenConfig(runtime).map(entry => [entry.key, entry.value]),
  );
  const keys = new Set([...runtimeEntries.keys(), ...dynamicEntries.keys()]);
  return [...keys]
    .sort((a, b) => a.localeCompare(b))
    .map(key => {
      const runtimeValue = runtimeEntries.get(key);
      const dynamicValue = dynamicEntries.get(key);
      const overridden =
        dynamicValue !== undefined &&
        runtimeValue !== undefined &&
        dynamicValue !== runtimeValue;
      return {
        key,
        value: runtimeValue ?? dynamicValue ?? '',
        dynamic: dynamicValue !== undefined,
        ...(overridden ? { dynamicValue } : {}),
      };
    });
}

/** True when the key or a dotted segment starts with the query (not a mid-string match). */
export function keyMatchesPrefix(key: string, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase('en-US');
  if (!needle) return true;
  const lower = key.toLocaleLowerCase('en-US');
  if (lower.startsWith(needle)) return true;
  return lower.split('.').some(segment => segment.startsWith(needle));
}

/** Prefix match on keys; substring match still allowed on values. */
export function filterConfigRows(
  rows: ConfigRow[],
  query: string,
  options: { dynamicOnly?: boolean } = {},
): ConfigRow[] {
  const needle = query.trim().toLocaleLowerCase('en-US');
  return rows.filter(row => {
    if (options.dynamicOnly && !row.dynamic) return false;
    if (!needle) return true;
    if (keyMatchesPrefix(row.key, needle)) return true;
    return [row.value, row.dynamicValue ?? ''].some(part =>
      part.toLocaleLowerCase('en-US').includes(needle),
    );
  });
}

function ConfigList({ rows }: { rows: ConfigRow[] }) {
  const classes = useStyles();
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No config entries.
      </Typography>
    );
  }
  return (
    <Box className={classes.list}>
      {rows.map(row => (
        <Box key={row.key} className={classes.row}>
          {row.dynamic ? (
            <span className={classes.dot} title="Dynamic config" />
          ) : (
            <span className={classes.dotSpacer} />
          )}
          <Typography className={classes.key}>{row.key}</Typography>
          <Typography className={classes.value}>
            {row.dynamicValue !== undefined ? (
              <>
                <span className={classes.runtimeValue}>{row.value}</span>
                {' → '}
                {row.dynamicValue}
              </>
            ) : (
              row.value
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Broker runtime and dynamic config from catalog annotations. */
export function PulsarClusterConfigContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations;
  const runtime = parseConfig(annotations?.[BROKER_RUNTIME_CONFIG_ANNOTATION]);
  const dynamic = parseConfig(annotations?.[BROKER_DYNAMIC_CONFIG_ANNOTATION]);
  const [query, setQuery] = useState('');
  const [dynamicOnly, setDynamicOnly] = useState(true);

  const rows = useMemo(
    () =>
      configRows(
        runtime.kind === 'ok' ? runtime.value : undefined,
        dynamic.kind === 'ok' ? dynamic.value : undefined,
      ),
    [runtime, dynamic],
  );
  const filteredRows = useMemo(
    () => filterConfigRows(rows, query, { dynamicOnly }),
    [rows, query, dynamicOnly],
  );
  const keyOptions = useMemo(
    () => filterConfigRows(rows, '', { dynamicOnly }).map(row => row.key),
    [rows, dynamicOnly],
  );

  if (runtime.kind === 'empty' && dynamic.kind === 'empty') {
    return (
      <EmptyState
        missing="data"
        title="No broker config"
        description={`Annotations ${BROKER_RUNTIME_CONFIG_ANNOTATION} and ${BROKER_DYNAMIC_CONFIG_ANNOTATION} are not set.`}
      />
    );
  }

  return (
    <InfoCard title="Broker config">
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.hint}
      >
        Green marks a dynamic value. Overrides show as runtime → dynamic.
      </Typography>
      <Box className={classes.filterRow}>
        <Autocomplete
          freeSolo
          options={keyOptions}
          filterOptions={(options, state) =>
            options.filter(option => keyMatchesPrefix(option, state.inputValue))
          }
          inputValue={query}
          onInputChange={(_event, value) => setQuery(value)}
          onChange={(_event, value) =>
            setQuery(typeof value === 'string' ? value : value ?? '')
          }
          className={classes.filter}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              label="Filter config"
              placeholder="Search by key or value"
            />
          )}
        />
        <FormControlLabel
          control={
            <Switch
              color="primary"
              size="small"
              checked={dynamicOnly}
              onChange={(_event, checked) => setDynamicOnly(checked)}
            />
          }
          label="Dynamic only"
        />
      </Box>
      {runtime.kind === 'error' && (
        <Typography color="error" gutterBottom>
          Invalid runtime JSON: {runtime.message}
        </Typography>
      )}
      {dynamic.kind === 'error' && (
        <Typography color="error" gutterBottom>
          Invalid dynamic JSON: {dynamic.message}
        </Typography>
      )}
      <ConfigList rows={filteredRows} />
    </InfoCard>
  );
}
